import { NextPageContext } from 'next';
import Head from 'next/head';
import { generateSimpleStructuredData } from 'common/utils';
import { generateCookieList } from 'common/utils/trackingTag';
import AboutPageLayout from 'components/v2/layouts/about-page-layout';
import CareersPageContent from 'components/v2/modules/careers-page/careers-content';
import { parse } from 'cookie';
import { getJobsCall, JobType } from 'service/careers';

const metadata = {
  title: 'PostPrime 採用情報｜金融・投資・経済のソーシャルメディアで働く',
  description:
    'PostPrimeは金融・投資・経済に特化したソーシャルメディアです。スタートアップの成長環境で未来を共に創る仲間を直接募集しています。',
  image: 'https://assets.postprime.com/internals/pp_og.png',
  url: 'https://postprime.com/about/careers',
  keywords:
    'PostPrime, キャリア, 採用情報, エンジニア, デザイナー, プロダクトマネージャー, 投資情報プラットフォーム, 投資体験革新, チーム参加, ポジション募集中, フィンテック, 金融テクノロジー, スタートアップ, クリエイター経済, 投資家コミュニティ, AI開発, データサイエンス, UXデザイン, フロントエンド, バックエンド, フルスタック, プロダクト開発, イノベーション, リモートワーク, フレックス勤務, 成長企業, 新卒採用, 中途採用, インターン',
};

export default function CareersPage({
  jobs,
}: {
  jobs: JobType[];
}): JSX.Element {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metadata.url} />
        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.description} />
        <meta property="og:image" content={metadata.image} key="og:image" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={metadata.title} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={metadata.url} />
        <meta property="twitter:title" content={metadata.title} />
        <meta property="twitter:description" content={metadata.description} />
        <meta property="twitter:image" content={metadata.image} />

        <meta name="keywords" content={metadata.keywords} />
        <link rel="canonical" href={metadata.url} />
        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: generateSimpleStructuredData(metadata),
          }}
        />
      </Head>
      <AboutPageLayout>
        <CareersPageContent jobs={jobs} />
      </AboutPageLayout>
    </>
  );
}

export const getServerSideProps = async (context: NextPageContext) => {
  const cookiesList = generateCookieList(
    context.query,
    parse(context.req?.headers?.cookie || ''),
  );
  if (cookiesList.length > 0) {
    context.res.setHeader('Set-Cookie', cookiesList);
  }
  try {
    const jobsResponse = await getJobsCall();
    return {
      props: {
        jobs: jobsResponse?.jobs,
      },
    };
  } catch (error) {
    return {
      props: {
        jobs: [],
      },
    };
  }
};
